import { getAuthUrl } from "../config/apiEndpoints";

export interface User {
  uuid: string;
  username: string;
  is_commissioner: boolean;
  must_change_password?: boolean;
}

export interface AdminUser {
  uuid: string;
  username: string;
  email: string | null;
  is_commissioner: boolean;
  is_active: boolean;
  must_change_password: boolean;
  date_joined: string | null;
  last_login: string | null;
}

export interface AuthResponse {
  user: User;
  access_token: string;
  refresh_token: string;
}

async function authRequest<T>(body: Record<string, unknown>): Promise<T> {
  const response = await fetch(getAuthUrl("/auth"), {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });

  const data = await response.json();

  if (!response.ok) {
    throw new Error(data.error || "Authentication failed");
  }

  return data;
}

export async function login(
  username: string,
  password: string
): Promise<AuthResponse> {
  return authRequest<AuthResponse>({
    request_type: "login",
    username,
    password,
  });
}

export async function changePassword(
  currentPassword: string,
  newPassword: string
): Promise<{ message: string }> {
  const token = getAccessToken();
  const response = await fetch(getAuthUrl("/auth"), {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({
      request_type: "change_password",
      current_password: currentPassword,
      new_password: newPassword,
    }),
  });

  const data = await response.json();
  if (!response.ok) {
    throw new Error(data.error || "Failed to change password");
  }
  return data;
}

export async function requestPasswordReset(
  email: string
): Promise<{ message: string }> {
  return authRequest<{ message: string }>({
    request_type: "request_password_reset",
    email,
  });
}

export async function resetPassword(
  email: string,
  code: string,
  newPassword: string
): Promise<{ message: string }> {
  return authRequest<{ message: string }>({
    request_type: "reset_password",
    email,
    code,
    new_password: newPassword,
  });
}

export async function adminCreateUser(
  username: string,
  email: string,
  password: string,
  isCommissioner: boolean
): Promise<{ user: AdminUser }> {
  const token = getAccessToken();
  const response = await fetch(getAuthUrl("/auth"), {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({
      request_type: "admin_create_user",
      username,
      email,
      password,
      is_commissioner: isCommissioner,
    }),
  });

  const data = await response.json();
  if (!response.ok) {
    throw new Error(data.error || "Failed to create user");
  }
  return data;
}

export async function adminListUsers(): Promise<{ users: AdminUser[] }> {
  const token = getAccessToken();
  const response = await fetch(getAuthUrl("/auth"), {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({
      request_type: "admin_list_users",
    }),
  });

  const data = await response.json();
  if (!response.ok) {
    throw new Error(data.error || "Failed to list users");
  }
  return data;
}

export async function adminSetUserActive(
  userUuid: string,
  isActive: boolean
): Promise<{ message: string }> {
  const token = getAccessToken();
  const response = await fetch(getAuthUrl("/auth"), {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({
      request_type: "admin_set_user_active",
      user_uuid: userUuid,
      is_active: isActive,
    }),
  });

  const data = await response.json();
  if (!response.ok) {
    throw new Error(data.error || "Failed to update user");
  }
  return data;
}

export async function refreshToken(
  refresh_token: string
): Promise<AuthResponse> {
  return authRequest<AuthResponse>({
    request_type: "refresh_token",
    refresh_token,
  });
}

export function logout(): void {
  localStorage.removeItem("access_token");
  localStorage.removeItem("refresh_token");
  localStorage.removeItem("user");
}

export function getStoredUser(): User | null {
  const userStr = localStorage.getItem("user");
  if (!userStr) return null;
  try {
    return JSON.parse(userStr);
  } catch {
    return null;
  }
}

export function getAccessToken(): string | null {
  return localStorage.getItem("access_token");
}

export function storeAuth(authResponse: AuthResponse): void {
  localStorage.setItem("access_token", authResponse.access_token);
  localStorage.setItem("refresh_token", authResponse.refresh_token);
  localStorage.setItem("user", JSON.stringify(authResponse.user));
}

export function isAuthenticated(): boolean {
  return !!getAccessToken();
}

export function isCommissioner(): boolean {
  const user = getStoredUser();
  return user?.is_commissioner ?? false;
}
